import {
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  ZERO_TIME_HHmm,
} from "../../constants";
import {
  createSuccessResult,
  IRideMetadata,
  IStationInfo,
  SimpleResult,
} from "../../dto";
import { TimeZoneEnum, WaypointCategoryEnum } from "../../enums";
import {
  IStationRouteResult,
  IStationRouteStationInfo,
  TTimeHHmm,
} from "../../models";
import {
  addSecondsToHHmmTimeString,
  calculateAbsDurationInSeconds,
  isValidDateTime,
} from "../date.helpers";
import {
  isStationRouteWaypointStation,
  stampRoutingWaypointsAsMlPath,
} from "../station-route";

export const toStationRouteWaypointStopFromRideStation = (
  station: IStationInfo,
): IStationRouteStationInfo => {
  const waypointStop: IStationRouteStationInfo = {
    id: station.id,
    name: station.name,
    locationInfo: station.locationInfo,
    areaId: station.areaId,
    // Ride/course waypoints become MLPath when persisted on a customized path.
    waypointCategory: WaypointCategoryEnum.MLPathWaypoint,
  };
  return waypointStop;
};

export const toStationRouteStationInfoFromRideStation = ({
  station,
  timeArrival,
}: {
  station: IStationInfo;
  timeArrival: TTimeHHmm;
}): IStationRouteStationInfo => {
  const stationInfo: IStationRouteStationInfo = {
    id: station.id,
    name: station.name,
    locationInfo: station.locationInfo,
    areaId: station.areaId,
    timeArrival,
    waypointCategory: isStationRouteWaypointStation(station)
      ? WaypointCategoryEnum.MLPathWaypoint
      : station.waypointCategory,
  };
  return stationInfo;
};

export const extractDurations = (
  stations: { arrivalTime?: TDateISO }[],
): number[] => {
  if (
    !stations?.length ||
    stations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION
  ) {
    return [];
  }

  return stations.slice(1).map((station, index) => {
    const currentArrival = station.arrivalTime;
    const previousArrival = stations[index].arrivalTime;
    if (!isValidDateTime(currentArrival) || !isValidDateTime(previousArrival)) {
      return 0;
    }
    return calculateAbsDurationInSeconds(currentArrival, previousArrival);
  });
};

export const transformRideMetadataToStationRouteResult = (
  metadata: IRideMetadata,
): SimpleResult<IStationRouteResult> => {
  const stationsInfo: IStationInfo[] = metadata.stationsInfo || [];

  const orderedStationIds = stationsInfo.map(station => station.id);
  const durations = extractDurations(stationsInfo);
  const distances = metadata.distances || [];
  const waypointStops = stationsInfo
    .filter(station => isStationRouteWaypointStation(station))
    .map(toStationRouteWaypointStopFromRideStation);

  const totalDuration = durations.reduce((acc, duration) => acc + duration, 0);
  const totalDistance =
    metadata.totalDistance ??
    distances.reduce((acc, distance) => acc + distance, 0);

  const stationRouteResult: IStationRouteResult = {
    orderedStationIds,
    durations,
    totalDuration,
    distances,
    totalDistance,
    routePolyline: metadata.routePolyline,
    waypointStops: waypointStops.length ? waypointStops : undefined,
  };
  return createSuccessResult(stationRouteResult);
};

export const getStationRouteInfoFromRideMetadata = (
  rideMetadata: IRideMetadata,
): IStationRouteStationInfo[] => {
  const stationsInfo = rideMetadata?.stationsInfo || [];
  const stationsTimed: IStationRouteStationInfo[] = [];
  let previousTimeArrival = ZERO_TIME_HHmm;
  const durations = extractDurations(stationsInfo);
  stationsInfo.forEach((station, index) => {
    let timeArrival = ZERO_TIME_HHmm;

    if (index === 0) {
      timeArrival = ZERO_TIME_HHmm;
    } else {
      timeArrival =
        addSecondsToHHmmTimeString(
          previousTimeArrival,
          durations[index - 1] || 0,
          TimeZoneEnum.UTC,
        ) || ZERO_TIME_HHmm;
    }

    stationsTimed.push(
      toStationRouteStationInfoFromRideStation({ station, timeArrival }),
    );

    previousTimeArrival = timeArrival;
  });

  return stampRoutingWaypointsAsMlPath(stationsTimed);
};
